import PromiseBB from "bluebird";
import * as path from "path";
import * as React from "react";
import { SITE_GAME_NAME } from "../../controls/constants";
import ProgressBar from "../../controls/ProgressBar";
import DateTimeFilter from "../../controls/table/DateTimeFilter";
import GameFilter from "../../controls/table/GameFilter";
import TextFilter from "../../controls/table/TextFilter";
import { Icon } from "../../controls/TooltipControls";
import * as fs from "../../util/fs";
import { getCurrentLanguage } from "../../util/i18n";
import { getSafe } from "../../util/storeHelper";
import { bytesToString, truthy } from "../../util/util";
import { SITE_ID } from "../gamemode_management/constants";
import { gameName } from "../gamemode_management/selectors";
import getDownloadGames from "./util/getDownloadGames";
import setDownloadGames from "./util/setDownloadGames";
import DownloadGameList from "./views/DownloadGameList";
import DownloadProgressFilter from "./views/DownloadProgressFilter";
import FileTime from "./views/FileTime";
function progress(props) {
    const { t, download } = props;
    const { state } = download;
    const received = download.received || 0;
    const verified = download.verified || 0;
    const size = download.size || 1;
    switch (state) {
        case "init":
            return React.createElement("span", null, t("Pending"));
        case "finished":
            return React.createElement("span", null, t("Finished"));
        case "failed":
            return React.createElement("span", null, t("Failed"));
        case "finalizing":
            return (React.createElement("div", { style: { display: "flex" } },
                React.createElement(ProgressBar, { labelLeft: t("Finalizing"), max: size, now: verified, showPercentage: true, style: { flex: "1 1 0" } })));
        case "redirect":
            return React.createElement("span", null, t("Redirected"));
        case "paused":
            return React.createElement("span", null, t("Paused"));
        default:
            return (React.createElement("div", { style: { display: "flex" } },
                React.createElement(ProgressBar, { max: size, now: received, showPercentage: true, showTimeLeft: true, style: { flex: "1 1 0" } }),
                !download.pausable ? (React.createElement(Icon, { name: "feedback-warning", tooltip: t("The download server doesn't support resuming downloads ") })) : null));
    }
}
function capitalize(input) {
    return input.charAt(0).toUpperCase() + input.slice(1);
}
function calc(props) {
    const { download } = props;
    const { state, received, size, verified } = download;
    if (state === "started") {
        return Math.floor((received / Math.max(size, 1)) * 100.0) / 100.0;
    }
    else if (state === "finalizing") {
        return Math.floor((verified / Math.max(size, 1)) * 100.0) / 100.0;
    }
    else {
        return state;
    }
}
function downloadTime(download) {
    return download.fileTime !== undefined
        ? new Date(download.fileTime)
        : undefined;
}
function nameFromUrl(input) {
    if (input === undefined) {
        return undefined;
    }
    const pathname = new URL(input).pathname;
    if (!truthy(pathname)) {
        return undefined;
    }
    try {
        return decodeURI(path.basename(pathname));
    }
    catch (err) {
        return path.basename(pathname);
    }
}
function createColumns(api, props, withAddInProgress) {
    const state = api.getState();
    const isModernLayout = state.settings.window.useModernLayout;
    let lang;
    let collator;
    const getCollator = (locale) => {
        if (collator === undefined || locale !== lang) {
            lang = locale;
            collator = new Intl.Collator(locale, { sensitivity: "base" });
        }
        return collator;
    };
    const onSetDownloadGames = (dlId, games) => {
        return PromiseBB.resolve(setDownloadGames(api, dlId, games, withAddInProgress));
    };
    return [
        {
            id: "filename",
            name: "Filename",
            description: "Filename of the download",
            icon: "",
            calc: (attributes) => attributes.localPath ||
                nameFromUrl(getSafe(attributes, ["urls", 0], undefined)),
            placement: "both",
            isToggleable: true,
            edit: {},
            isSortable: true,
            sortFunc: (lhs, rhs, locale) => getCollator(locale).compare(lhs, rhs),
            filter: new TextFilter(true),
        },
        {
            id: "logicalname",
            name: "Name",
            description: "Readable Name",
            icon: "",
            calc: (attributes) => getSafe(attributes, ["modInfo", "name"], "") || attributes.localPath,
            placement: "both",
            isToggleable: true,
            edit: {},
            isSortable: true,
            sortFunc: (lhs, rhs, locale) => getCollator(locale).compare(lhs, rhs),
            isDefaultVisible: false,
            filter: new TextFilter(true),
        },
        {
            id: "game",
            name: "Game",
            description: "The game(s) this download is associated with",
            help: "You can associate a download with multiple compatible games so it will show up " +
                "when managing those games as well.",
            icon: "game",
            isDefaultVisible: !isModernLayout,
            customRenderer: (download, detailCell, t) => {
                const { downloads, knownGames } = props();
                const { store } = api;
                // TODO: awkward!
                const id = Object.keys(downloads).find((dlId) => downloads[dlId] === download);
                if (detailCell) {
                    return (React.createElement(DownloadGameList, { currentGames: getDownloadGames(download), games: knownGames, id: id, t: t, onSetDownloadGames: onSetDownloadGames }));
                }
                else {
                    const games = getDownloadGames(download);
                    const name = games[0] === SITE_ID
                        ? t(SITE_GAME_NAME)
                        : gameName(store.getState(), games[0]);
                    const more = games.length > 1 ? "..." : "";
                    return (React.createElement("div", null,
                        name,
                        more));
                }
            },
            calc: (attributes) => getDownloadGames(attributes),
            placement: "both",
            isToggleable: true,
            edit: {},
            isSortable: true,
            isGroupable: true,
            filter: isModernLayout ? undefined : new GameFilter(),
            sortFunc: (lhs, rhs, locale) => getCollator(locale).compare(lhs, rhs),
        },
        {
            id: "filetime",
            name: "Downloaded",
            description: "Time the file was last modified",
            icon: "calendar-plus-o",
            customRenderer: (attributes, detail, t) => {
                const { gameMode, downloadPath } = props();
                const time = downloadTime(attributes);
                if (time === undefined &&
                    (getDownloadGames(attributes)[0] !== gameMode ||
                        attributes.localPath === undefined)) {
                    return null;
                }
                return (React.createElement(FileTime, { detail: detail, download: attributes, downloadPath: downloadPath, language: getCurrentLanguage(), t: t, time: time }));
            },
            calc: (attributes) => {
                const { downloadPath, downloads, gameMode, onSetAttribute } = props();
                const time = downloadTime(attributes);
                if (time !== undefined) {
                    return time;
                }
                if (getDownloadGames(attributes)[0] !== gameMode ||
                    attributes.localPath === undefined) {
                    return null;
                }
                return fs
                    .statAsync(path.join(downloadPath, attributes.localPath))
                    .then((stat) => {
                    const id = Object.keys(downloads).find((key) => downloads[key] === attributes);
                    onSetAttribute(id, stat.mtimeMs);
                    return PromiseBB.resolve(stat.mtime);
                })
                    .catch(() => undefined);
            },
            placement: "both",
            isToggleable: true,
            edit: {},
            isSortable: true,
            isDefaultSort: "desc",
            filter: new DateTimeFilter(),
        },
        {
            id: "filesize",
            name: "File Size",
            description: "Total size of the file",
            icon: "chart-bars",
            customRenderer: (download, detailCell, t) => (React.createElement("span", null, download.size !== undefined ? bytesToString(download.size) : "???")),
            calc: (download) => download.size,
            placement: "table",
            isToggleable: true,
            edit: {},
            isSortable: true,
        },
        {
            id: "progress",
            name: "Progress",
            description: "Download progress",
            icon: "clock-o",
            customRenderer: (download, detailCell, t) => progress({ download, t }),
            calc: (download, t) => calc({ download, t }),
            placement: "table",
            isToggleable: true,
            edit: {},
            isSortable: true,
            isGroupable: (download, t) => t(capitalize(download.state ?? "init")),
            filter: new DownloadProgressFilter(),
        },
    ];
}
export default createColumns;
